import * as React from 'react';
import { PublicState } from 'rheostat';
import 'rheostat/css/slider.css';
import 'rheostat/css/slider-horizontal.css';

import WorkflowStep from '../WorkflowStep';
import QuotePlan from '../QuotePlan';
import { Button } from '../../components/buttons';
import { IconChevronLeft, IconAdd, IconRemove } from '../../components/icons';
import { FormCheckConditional } from '../../components/forms';
import CalculatorExplanation from './CalculatorExplanation';

interface Props {
	currentUser: UserDetails;
	assessment: any;
	customerDetails: CustomerDemographicDetails;
	customerFamilyList: CustomerFamily[];
	user: UserDetails;
	onCustomerPolicySelect: (plan: CustomerPolicy) => void;
	customerPolicy: CustomerPolicyDto;
	planOptions: CustomerPolicyDto[];
	history: any;
	match: any;
	actions: any;
}
interface State {
	rate: number;
	isLoading: boolean;
	section0: Section;
	section1: Section;
}
interface Section {
	expanded: boolean;
}
class AdvisorySelectYourPlanPage extends React.Component<Props, State> {
	state = {
		rate: 0,
		isLoading: true,
		section0: {
			expanded: false
		},
		section1: {
			expanded: false
		}
	};

	private COVERAGE_MAX = 1000000;
	private COVERAGE_MIN = 25000;
	private COVERAGE_DEFAULT = Math.round((this.COVERAGE_MAX - this.COVERAGE_MIN) / 2 + this.COVERAGE_MIN);
	private TERM_MAX = 65 - (this.props.customerDetails.age || 0);
	private TERM_MIN = this.props.currentUser.isAuthenticated ? 5 : 10;
	private TERM_DEFAULT = Math.round((this.TERM_MAX - this.TERM_MIN) / 2 + this.TERM_MIN);

	componentDidMount() {
		this.setState({ isLoading: true });
		const { gender, id } = this.props.user;
		const { isSmoker, age } = this.props.customerDetails;

		if (age == null || age < 18 || age > 65 || gender == null || isSmoker == null) {
			this.props.history.replace(`/quote/${this.props.match.params.workflow}/about-you`);
		}
		else {
			const params: GetAdvisoryPoliciesRequest = {
				gender: gender,
				age,
				customerDemographicDetailsJson: this.props.customerDetails as string,
				childrenAge: this.props.assessment.childrenAge.map((a: number) => !a ? 0 : a).join(',') as number[],
				userId: id
			};

			this.props.actions.fetchAdvisoryPlanOptions(params)
				.then(() => {
					const { coverageAmount, term, recommendationExceedsOfferLimit, exceedsTwoMillions,
						millionExpression } = this.props.planOptions[0];
					const customerPolicy = {
						...this.props.customerPolicy,
						coverageAmount: coverageAmount || this.COVERAGE_DEFAULT,
						term: term || this.TERM_DEFAULT,
						recommendationExceedsOfferLimit: recommendationExceedsOfferLimit,
						exceedsTwoMillions: exceedsTwoMillions,
						millionExpression: millionExpression
					};
					this.props.actions.setCustomerPolicy(customerPolicy);
					if (recommendationExceedsOfferLimit) {
						this.onSectionToggle('section0', 'expanded');
					}
					this.setState({ isLoading: false });
				});
		}
	}

	onCoverageUpdate = (slider: PublicState) => {
		// Rheostat uses a values list to support multiple handles. Since we only have one handle, take the first index.
		const valuePct = slider.values[0] / 100;
		const customerPolicy = {
			...this.props.customerPolicy,
			coverageAmount: Math.round(this.COVERAGE_MAX * valuePct)
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}

	onTermUpdate = (slider: PublicState) => {
		// Rheostat uses a values list to support multiple handles. Since we only have one handle, take the first index.
		const valuePct = slider.values[0] / 100;
		const customerPolicy = {
			...this.props.customerPolicy,
			term: Math.round(this.TERM_MAX * valuePct)
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}

	onSliderDragEnd = () => {
		this.setState({ isLoading: true });
		const { coverageAmount, term } = this.props.customerPolicy;
		const { gender, id } = this.props.user;
		const { age } = this.props.customerDetails;

		const params: GetAdvisoryPoliciesRequest = {
			coverageAmount: coverageAmount,
			term: term,
			gender: gender,
			// birthdate: birthDate,
			age,
			customerDemographicDetailsJson: this.props.customerDetails as string,
			childrenAge: this.props.assessment.childrenAge.map((a: number) => !a ? 0 : a).join(',') as number[],
			userId: id
		};
		this.props.actions.fetchAdvisoryPlanOptions(params)
			.then(() => this.setState({ isLoading: false }));
	}

	back = () => {
		this.props.history.push(`/quote/${this.props.match.params.workflow}/your-needs`);
	}

	next = () => {
		const customerPolicy = {
			...this.props.customerPolicy,
			rate: this.state.rate
		};

		this.props.onCustomerPolicySelect(customerPolicy);
	}

	toCustomPolicy = () => {
		const policy = this.props.planOptions[0];
		this.props.actions.setCustomerPolicy(policy);
		this.props.history.push(`/quote/Custom/select-your-policy`);
	}

	onSectionToggle = (section: any, expanded: any) => {
		this.setState({
			[section]: { ...this.state[section], [expanded]: !this.state[section][expanded] }
		});
	}

	render() {
		const { ...props } = this.props;
		// make sure the customer can have a policy within min term
		if (65 - this.TERM_MIN < this.props.customerDetails.age! || this.props.planOptions.length === 0) {
			return (
				<WorkflowStep title="Sorry, we currently do not have any plans available for you." />
			);
		}
		else {
			return (
				<WorkflowStep title="Based on your inputs, we recommend the following policy.">
					<div>
						<div className="quote-plans text-center">
							<QuotePlan
								workflow={this.props.match.params.workflow}
								option={props.planOptions![0]}
								onCustomerPolicySelect={this.props.onCustomerPolicySelect}
								isLoading={this.state.isLoading}
								actionChildren={
									<Button className="btn-link text-primary btn-block text-wrap" onClick={this.toCustomPolicy}>
										<em>Customize coverage amount or term</em>
									</Button>}
							/>
						</div>
						<div className="text-center">
							<em>Rates may change based on lifestyle</em>
						</div>
						<div className="mt-3">
							<h3>
								<Button
									className="btn-link text-primary btn-block"
									onClick={() => this.onSectionToggle('section0', 'expanded')}
								>
									{this.state.section0.expanded ? <IconRemove /> : <IconAdd />}
									<span>How we calculated this</span>
								</Button>
							</h3>
							<FormCheckConditional isOpen={this.state.section0.expanded}>
								<CalculatorExplanation
									recommendationExceedsOfferLimit={this.props.customerPolicy.recommendationExceedsOfferLimit}
									exceedsTwoMillions={this.props.customerPolicy.exceedsTwoMillions}
									millionExpression={this.props.customerPolicy.millionExpression}
								/>
							</FormCheckConditional>

							<h3>
								<Button
									className="btn-link text-primary btn-block"
									onClick={() => this.onSectionToggle('section1', 'expanded')}
								>
									{this.state.section1.expanded ? <IconRemove /> : <IconAdd />}
									<span>View Legal Disclaimers</span>
								</Button>
							</h3>
							<FormCheckConditional isOpen={this.state.section1.expanded}>
								<p className="mb-0">
								The results you received from this calculator are for your information only and are not intended to provide a 
								detailed analysis of your life insurance needs.  We are not responsible for any losses or damages arising from 
								errors or omissions in the analysis or your reliance on it.
								</p>
							</FormCheckConditional>
						</div>
					</div>
					<div className="workflow-progress-actions">
						<Button onClick={this.back} className="btn-outline-primary workflow-progress-action">
							<IconChevronLeft />
							<span className="align-middle mx-2 d-none d-sm-inline-block">Back</span>
						</Button>
					</div>
				</WorkflowStep>
			);
		}
	}
}

export default AdvisorySelectYourPlanPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/advisory/AdvisorySelectYourPlanPage.tsx